import { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { AnalyticsEvent, Intercom, useAnalytics } from '@bonusly/analytics';
import { Button, Display, Modal, ModalBody, ModalContent, ModalFooter, ModalOverlay, Stack, Text, useDisclosure } from '@bonusly/design-system';
import { useDispatch } from '@bonusly/events';
import { useLocalStorage } from '@bonusly/storage';
import { useCurrentCompany, useCurrentUser } from '@bonusly/who-am-i';

import { VimeoEmbed } from '../vimeo-embed/vimeo-embed';

const DISMISS_KEY = 'bonusly-onboarding-welcome-modal-dismissed';

const WELCOME_MODAL_VIDEO_ID = '1125881049';

const ACTIONS_BY_ROLE = {
	admin: {
		dismissEvent: AnalyticsEvent.Onboarding.WelcomeAdminModalDismissed,
		clickedEvent: AnalyticsEvent.Onboarding.WelcomeAdminModalInviteClicked,
		viewedEvent: AnalyticsEvent.Onboarding.WelcomeAdminModalViewed,
		dismissChecklist: Intercom.Checklists.OnboardingAdminTierAlpha,
	},
	user: {
		dismissEvent: AnalyticsEvent.Onboarding.WelcomeModalDismissed,
		clickedEvent: AnalyticsEvent.Onboarding.WelcomeModalInviteClicked,
		viewedEvent: AnalyticsEvent.Onboarding.WelcomeModalViewed,
		dismissChecklist: Intercom.Checklists.OnboardingUserTierAlpha,
		tour: Intercom.Tours.RecognitionUser,
	},
};

export function WelcomeModal() {
	const { t } = useTranslation('onboarding', { keyPrefix: 'welcome_modal' });
	const company = useCurrentCompany();
	const user = useCurrentUser();
	const [dismissed, setDismissed] = useLocalStorage(DISMISS_KEY, false);
	const [searchParams] = useSearchParams();
	const viewedTrackedRef = useRef(false);
	const showWelcomeModal = searchParams.get('welcome_modal');
	const defaultIsOpen = !dismissed && showWelcomeModal === 'true';
	const { isOpen, onClose } = useDisclosure({ defaultIsOpen });
	const { trackEvent } = useAnalytics();
	const dispatch = useDispatch();

	const role = user.admin ? 'admin' : 'user';
	const actionByRole = ACTIONS_BY_ROLE[role];
	const permitted = !company.is_superadmin_initialized && company.status === 'in_trial';

	const onCloseAndDismiss = useCallback(() => {
		setDismissed(true);
		onClose();
	}, [onClose, setDismissed]);

	const onDismiss = useCallback(() => {
		trackEvent(actionByRole.dismissEvent);
		Intercom.startChecklist(actionByRole.dismissChecklist);
		onCloseAndDismiss();
	}, [actionByRole, onCloseAndDismiss, trackEvent]);

	const inviteOrStartTour = useCallback(() => {
		user.admin ? dispatch('open-user-invite-modal') : Intercom.startTour(Intercom.Tours.RecognitionUser);
	}, [dispatch, user]);

	const onAction = useCallback(() => {
		trackEvent(actionByRole.clickedEvent);
		onCloseAndDismiss();
		inviteOrStartTour();
	}, [actionByRole, inviteOrStartTour, onCloseAndDismiss, trackEvent]);

	useEffect(() => {
		if (!isOpen) return;
		if (!permitted || dismissed || !trackEvent) return;
		if (viewedTrackedRef.current) return;

		viewedTrackedRef.current = true;
		trackEvent(actionByRole.viewedEvent);
	}, [actionByRole, dismissed, permitted, trackEvent, isOpen]);

	// if is superadmin initialized or company not in trial
	if (!permitted || dismissed) return null;

	return (
		<Modal closeOnOverlayClick={false} isCentered isOpen={isOpen} onClose={onClose} size="lg">
			<ModalOverlay />
			<ModalContent pb={2} pt={4}>
				<ModalBody as={Stack} gap={3}>
					<VimeoEmbed videoId={WELCOME_MODAL_VIDEO_ID} height={284} />
					<Display fontWeight="extrabold" fontSize={300} mt={2}>
						{t('title')}
					</Display>
					<Text fontSize={50}>{t(`${role}_tier_alpha_line_1`)}</Text>
					<Text fontSize={50}>{t(`${role}_tier_alpha_line_2`)}</Text>
				</ModalBody>

				<ModalFooter gap={2} justifyContent="center">
					<Button colorScheme="secondary" onClick={onDismiss} size="md" tabIndex={2} variant="alpha">
						{t('dismiss')}
					</Button>
					<Button colorScheme="primary" onClick={onAction} size="md" tabIndex={1} variant="solid">
						{t(`${role}_cta`)}
					</Button>
				</ModalFooter>
			</ModalContent>
		</Modal>
	);
}
